import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Illustration, type IllustrationName } from '../illustrations/Illustration';

export type EmptyStateProps = {
  /** A sticker from the design system's set. Backend errors use `offline`. */
  illustration?: IllustrationName;
  title: ReactNode;
  /** One sentence: why it is empty or what went wrong. */
  children?: ReactNode;
  /** The one action that fixes it (a Button). */
  action?: ReactNode;
  /** Announce it (role="status") when it replaces content after a search or an error. */
  announce?: boolean;
  /** `error`: a backend failure, said plainly; defaults to the offline sticker and announces itself. Give it a Retry action. */
  kind?: 'empty' | 'error';
  /** The title's heading level: 3 inside a page (default); 1 when the empty state is the whole page (error and not-found pages). */
  headingLevel?: 1 | 2 | 3;
  className?: string;
};

/** What a screen shows when it has nothing yet: a sticker, a title, one sentence and one action. */
export function EmptyState({
  illustration: chosen,
  title,
  children,
  action,
  announce: announceProp,
  kind = 'empty',
  headingLevel = 3,
  className,
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as const;
  const illustration = chosen ?? (kind === 'error' ? 'offline' : undefined);
  const announce = announceProp ?? kind === 'error';
  return (
    <div
      role={announce ? 'status' : undefined}
      className={cx('flex flex-col items-center gap-3 px-6 py-10 text-center', className)}
    >
      {illustration ? <Illustration name={illustration} size="md" /> : null}
      <Heading className="type-title text-jc-ink">{title}</Heading>
      {children ? <p className="max-w-sm text-sm text-jc-ink-secondary">{children}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
